// HeroBanner - VERSIÓN MEJORADA CON RESPONSIVIDAD MÓVIL OPTIMIZADA
// Mantiene paleta de colores y funcionalidades principales con mejoras específicas para móvil

import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../supabaseClient';

const HeroBanner = ({ onPurchaseClick, isRaffleActive = true }) => {
  const [parallaxOffset, setParallaxOffset] = useState(0);
  
  // Estados para datos del sorteo
  const [raffleData, setRaffleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flyerLoaded, setFlyerLoaded] = useState(false);
  
  // Estado para la mayor compra
  const [maxPurchaseData, setMaxPurchaseData] = useState(null);
  const [loadingPurchase, setLoadingPurchase] = useState(true);

  // Efecto Parallax
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.pageYOffset;
      const parallax = scrolled * 0.5;
      setParallaxOffset(parallax);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Efecto para cargar los datos del sorteo activo al montar el componente
  useEffect(() => {
    const fetchRaffleData = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('raffles')
          .select('*')
          .eq('estado', 'activo')
          .single();

        if (error) throw error;

        setRaffleData(data);
      } catch (err) {
        console.error('Error al cargar los datos del sorteo para el banner:', err);
        setError(err.message || 'Error desconocido');
      } finally {
        setLoading(false);
      }
    };

    fetchRaffleData();
  }, []);

  // Efecto para cargar la mayor compra
  useEffect(() => {
    const fetchMaxPurchase = async () => {
      if (!raffleData?.id) return;
      
      try {
        setLoadingPurchase(true);
        
        // Consulta para obtener la mayor compra de boletos
        const { data, error } = await supabase
          .from('purchases')
          .select('cantidad_boletos, created_at')
          .eq('raffle_id', raffleData.id)
          .order('cantidad_boletos', { ascending: false })
          .limit(1)
          .single();

        if (error && error.code !== 'PGRST116') {
          // PGRST116 es el código de "no rows returned", no es un error real
          throw error;
        }

        if (data) {
          setMaxPurchaseData({
            cantidad: data.cantidad_boletos,
            fecha: new Date(data.created_at).toLocaleDateString('es-VE', {
              day: 'numeric',
              month: 'short',
              year: 'numeric'
            })
          });
        }
      } catch (err) {
        console.error('Error al cargar la mayor compra:', err);
        // No establecemos error aquí, simplemente no mostramos la información
      } finally {
        setLoadingPurchase(false);
      }
    };

    fetchMaxPurchase();
  }, [raffleData?.id]);

  // Estados de error y carga
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <p className="text-xl text-error">⚠️ Error al cargar la información del sorteo para el banner.</p>
        <p className="mt-2 text-sm text-gray-600">{error}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-secondary mx-auto"></div>
        <p className="mt-4 text-lg">Cargando banner del sorteo...</p>
      </div>
    );
  }

  if (!raffleData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-xl text-warning">ℹ️ No hay ningún sorteo activo en este momento.</p>
      </div>
    );
  }

  // ASIGNACIÓN DE DATOS DINÁMICOS
  const displayData = {
    title: raffleData?.titulo || "Gran Premio Áurea Fortuna 2024",
    mainPrize: raffleData?.premio_principal || "Bs. 0.00",
    secondPrize: raffleData?.premio_secundario || "Bs. 0.00",
    thirdPrize: raffleData?.premio_tercero || "Bs. 0.00",
    drawDate: raffleData?.fecha_sorteo ? new Date(raffleData.fecha_sorteo).toLocaleDateString('es-VE', { day: 'numeric', month: 'short', year: 'numeric' }) : "Fecha Desconocida",
    ticketPrice: raffleData?.precio_boleto || 50,
    // PRIORIDAD: flyer-1.jpg primero, luego fallback a Supabase
    image: '/assets/flyers/flyer-1.jpg', // 🎯 FLYER PRINCIPAL
    fallbackImage: raffleData?.imagen_url || "https://images.unsplash.com/photo-1699817586371-96a3837f0c97",
    imageAlt: "Imagen del Premio Principal"
  };

  // CÁLCULO DE ESTADÍSTICAS
  const ticketsSold = raffleData?.boletos_vendidos || 0;
  const totalTickets = raffleData?.boletos_totales || 1000;
  const availableTickets = totalTickets - ticketsSold;

  // RENDERIZACIÓN MEJORADA CON RESPONSIVIDAD MÓVIL OPTIMIZADA
  return (
    <div className="relative min-h-screen bg-gradient-to-br from-background via-navy/90 to-background overflow-hidden">
      {/* Parallax Background */}
      <div
        className="absolute inset-0 opacity-20"
        style={{ transform: `translateY(${parallaxOffset}px)` }}>
        <div className="absolute inset-0 bg-gradient-to-r from-secondary/30 via-transparent to-golden/30"></div>
        <div className="absolute top-20 left-10 w-32 h-32 bg-secondary/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-golden/20 rounded-full blur-3xl"></div>
      </div>
      
      {/* Content Container - RESPONSIVIDAD MEJORADA PARA MÓVIL + CORRECCIÓN HEADER */}
      <div className="relative z-10 w-full px-3 sm:px-4 lg:px-6 xl:px-8 pt-20 sm:pt-24 lg:pt-28 xl:pt-32 pb-6 sm:pb-8 lg:pb-12">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 xl:gap-12 items-start lg:items-center min-h-[calc(100vh-8rem)] lg:min-h-[calc(100vh-10rem)]">
          
          {/* Left Content - Información Principal */}
          <div className="text-center lg:text-left space-y-4 sm:space-y-6 lg:space-y-8 xl:space-y-10 pl-2 sm:pl-4 lg:pl-6 xl:pl-12">
            
            {/* Main Title - RESPONSIVE OPTIMIZADO PARA MÓVIL */}
            <div className="space-y-3 sm:space-y-4">
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-heading font-bold text-foreground leading-tight">
                {displayData?.title}
              </h1>
              <p className="text-sm sm:text-base md:text-lg text-text-secondary font-body max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                Tu oportunidad de ganar premios increíbles con la lotería más confiable de Venezuela
              </p>
            </div>

            {/* Premio Principal Destacado - RESPONSIVE OPTIMIZADO PARA MÓVIL */}
            <div className="bg-gradient-to-br from-card/90 to-card/70 backdrop-blur-sm rounded-xl lg:rounded-2xl xl:rounded-3xl p-3 sm:p-4 lg:p-6 xl:p-8 2xl:p-10 border border-golden/30 shadow-2xl relative overflow-hidden">
              {/* Efecto de brillo */}
              <div className="absolute inset-0 bg-gradient-to-r from-secondary/5 via-transparent to-golden/5"></div>
              
              <div className="relative text-center space-y-3 sm:space-y-4 lg:space-y-6">
                <div className="flex items-center justify-center space-x-2 sm:space-x-3 mb-3 sm:mb-4 lg:mb-6 xl:mb-8">
                  <Icon name="Trophy" size={20} className="text-secondary sm:w-6 sm:h-6 lg:w-7 lg:h-7 xl:w-8 xl:h-8" />
                  <span className="text-base sm:text-lg lg:text-xl xl:text-2xl font-heading font-bold text-foreground">
                    Gran Premio
                  </span>
                </div>
                
                {/* Premio Principal - RESPONSIVE OPTIMIZADO PARA MÓVIL */}
                <div className="space-y-2 sm:space-y-3">
                  <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-heading font-bold text-secondary drop-shadow-lg leading-tight">
                    {displayData?.mainPrize}
                  </div>
                  <div className="text-xs sm:text-sm lg:text-base xl:text-lg text-text-secondary font-medium">
                    Premio Principal
                  </div>
                </div>
                
                {/* Premios Secundarios - RESPONSIVE OPTIMIZADO PARA MÓVIL */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6 pt-3 sm:pt-4 lg:pt-6 xl:pt-8 border-t border-golden/20">
                  <div className="text-center space-y-1 sm:space-y-2">
                    <div className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-heading font-bold text-white"> 
                      {displayData?.secondPrize}
                    </div>
                    <div className="text-xs sm:text-sm lg:text-base text-text-secondary font-medium">
                      2° Premio (Mayor Compra)
                    </div>
                  </div>
                  <div className="text-center space-y-1 sm:space-y-2">
                    <div className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-heading font-bold text-white"> 
                      {displayData?.thirdPrize}
                    </div>
                    <div className="text-xs sm:text-sm lg:text-base text-text-secondary font-medium">
                      3° Premio
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 🎯 INFORMACIÓN DEL SORTEO - RESPONSIVE OPTIMIZADA PARA MÓVIL */}
            <div className="space-y-4 sm:space-y-6 lg:space-y-8 xl:space-y-10">
              
              {/* LAPTOP: Layout que USA TODO EL ANCHO DE LA PANTALLA */}
              <div className="hidden lg:block w-full">
                
                {/* Barra que se extiende por TODA la pantalla con separaciones visuales */}
                <div className="w-full bg-card/60 backdrop-blur-sm rounded-2xl border border-golden/20 shadow-xl overflow-hidden">
                  
                  {/* Flexbox que distribuye elementos equitativamente */}
                  <div className="flex w-full">
                    
                    {/* Fecha del Sorteo - IZQUIERDA (1/3 del ancho) */}
                    <div className="flex-1 flex items-center justify-center space-x-3 lg:space-x-4 p-4 lg:p-6 xl:p-8 border-r border-golden/30">
                      <div className="p-2 lg:p-3 bg-secondary/20 rounded-xl border border-secondary/30">
                        <Icon name="Calendar" size={20} className="text-secondary lg:w-7 lg:h-7" />
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-text-secondary/70 font-semibold uppercase tracking-wider mb-2">Fecha del Sorteo</div>
                        <div className="font-bold text-sm lg:text-lg text-foreground leading-tight">{displayData?.drawDate}</div>
                      </div>
                    </div>
                    
                    {/* Precio por Boleto - CENTRO (1/3 del ancho) */}
                    <div className="flex-1 flex items-center justify-center space-x-3 lg:space-x-4 p-4 lg:p-6 xl:p-8 border-r border-golden/30">
                      <div className="p-2 lg:p-3 bg-secondary/20 rounded-xl border border-secondary/30">
                        <Icon name="Ticket" size={20} className="text-secondary lg:w-7 lg:h-7" />
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-text-secondary/70 font-semibold uppercase tracking-wider mb-2">Precio por Boleto</div>
                        <div className="font-bold text-sm lg:text-lg text-foreground">Bs. {displayData?.ticketPrice?.toLocaleString()}</div>
                      </div>
                    </div>

                    {/* 🏆 Mayor Compra - DERECHA (1/3 del ancho) */}
                    {maxPurchaseData && (
                      <div className="flex-1 flex items-center justify-center space-x-3 lg:space-x-4 p-4 lg:p-6 xl:p-8">
                        <div className="p-2 lg:p-3 bg-gradient-to-br from-secondary/30 to-amber-500/30 rounded-xl border-2 border-secondary/40">
                          <Icon name="TrendingUp" size={20} className="text-secondary lg:w-7 lg:h-7" />
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-text-secondary/70 font-semibold uppercase tracking-wider mb-2">🏆 Mayor Compra</div>
                          <div className="font-bold text-sm lg:text-lg text-secondary leading-tight">
                            {maxPurchaseData.cantidad} boletos
                          </div>
                          <div className="text-xs text-text-secondary/60 mt-1">
                            {maxPurchaseData.fecha}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Botón CENTRADO - RESPONSIVE OPTIMIZADO PARA MÓVIL */}
                <div className="flex justify-center mt-6 lg:mt-8 xl:mt-10">
                  <Button
                    variant="secondary"
                    size="lg"
                    onClick={onPurchaseClick}
                    disabled={!isRaffleActive}
                    iconName="ShoppingCart"
                    iconPosition="left"
                    className="text-sm lg:text-lg xl:text-xl px-6 lg:px-8 xl:px-10 2xl:px-12 py-3 lg:py-4 xl:py-5 2xl:py-6 bg-gradient-to-r from-secondary to-amber-500 hover:from-secondary/90 hover:to-amber-400 text-gray-900 font-bold rounded-lg lg:rounded-xl xl:rounded-2xl shadow-2xl transform hover:scale-105 transition-all duration-300">

                    {isRaffleActive ? 'Comprar Boletos Ahora' : 'Sorteo Finalizado'}
                  </Button>
                </div>

                {/* Descripción CENTRADA - RESPONSIVE OPTIMIZADA PARA MÓVIL */}
                <div className="flex justify-center mt-3 lg:mt-4 xl:mt-6">
                  <p className="text-xs sm:text-sm text-text-secondary font-medium text-center max-w-2xl px-2 sm:px-4">
                    * Sorteo sujeto a términos y condiciones. Debe ser mayor de 18 años para participar.
                  </p>
                </div>
              </div>

              {/* TABLET: Layout de 2 columnas - RESPONSIVE OPTIMIZADO PARA MÓVIL */}
              <div className="hidden md:grid lg:hidden grid-cols-1 gap-3 sm:gap-4 lg:gap-6">
                <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:gap-4">
                  <div className="flex items-center space-x-2 sm:space-x-3 bg-card/60 backdrop-blur-sm rounded-xl lg:rounded-2xl px-3 sm:px-4 lg:px-5 py-2 sm:py-3 lg:py-4 border border-golden/20 shadow-lg">
                    <div className="p-1.5 sm:p-2 lg:p-3 bg-secondary/20 rounded-xl flex-shrink-0">
                      <Icon name="Calendar" size={16} className="text-secondary sm:w-4 sm:h-4 lg:w-5 lg:h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs text-text-secondary/70 font-medium uppercase tracking-wide">Fecha</div>
                      <div className="font-semibold text-xs sm:text-sm lg:text-base truncate">{displayData?.drawDate}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 sm:space-x-3 bg-card/60 backdrop-blur-sm rounded-xl lg:rounded-2xl px-3 sm:px-4 lg:px-5 py-2 sm:py-3 lg:py-4 border border-golden/20 shadow-lg">
                    <div className="p-1.5 sm:p-2 lg:p-3 bg-secondary/20 rounded-xl flex-shrink-0">
                      <Icon name="Ticket" size={16} className="text-secondary sm:w-4 sm:h-4 lg:w-5 lg:h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs text-text-secondary/70 font-medium uppercase tracking-wide">Precio</div>
                      <div className="font-semibold text-xs sm:text-sm lg:text-base">Bs. {displayData?.ticketPrice?.toLocaleString()}</div>
                    </div>
                  </div>
                </div>

                {/* 🏆 MAYOR COMPRA - TABLET */}
                {maxPurchaseData && (
                  <div className="flex items-center justify-center space-x-2 sm:space-x-3 bg-gradient-to-r from-secondary/20 to-amber-500/20 backdrop-blur-sm rounded-xl lg:rounded-2xl px-3 sm:px-4 lg:px-5 py-2 sm:py-3 lg:py-4 border-2 border-secondary/40 shadow-lg">
                    <div className="p-1.5 sm:p-2 lg:p-3 bg-secondary/30 rounded-xl">
                      <Icon name="TrendingUp" size={16} className="text-secondary sm:w-4 sm:h-4 lg:w-5 lg:h-5" />
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-text-secondary/70 font-medium uppercase tracking-wide">🏆 Mayor Compra para 2° Premio</div>
                      <div className="font-semibold text-sm sm:text-base lg:text-lg text-secondary">{maxPurchaseData.cantidad} boletos</div>
                      <div className="text-xs text-text-secondary/60">{maxPurchaseData.fecha}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile: Layout vertical optimizado - RESPONSIVE OPTIMIZADO PARA MÓVIL */}
              <div className="md:hidden space-y-3 sm:space-y-4">
                <div className="flex items-center space-x-3 sm:space-x-4 bg-card/70 backdrop-blur-sm rounded-xl px-4 py-3 border border-golden/20 shadow-lg">
                  <div className="p-2 bg-secondary/20 rounded-xl flex-shrink-0">
                    <Icon name="Calendar" size={18} className="text-secondary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-text-secondary/70 font-medium uppercase tracking-wide">Fecha del Sorteo</div>
                    <div className="font-semibold text-sm text-foreground leading-tight">{displayData?.drawDate}</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3 sm:space-x-4 bg-card/70 backdrop-blur-sm rounded-xl px-4 py-3 border border-golden/20 shadow-lg">
                  <div className="p-2 bg-secondary/20 rounded-xl flex-shrink-0">
                    <Icon name="Ticket" size={18} className="text-secondary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-text-secondary/70 font-medium uppercase tracking-wide">Precio por Boleto</div>
                    <div className="font-semibold text-sm text-foreground">Bs. {displayData?.ticketPrice?.toLocaleString()}</div>
                  </div>
                </div>

                {/* 🏆 MAYOR COMPRA - MÓVIL */}
                {maxPurchaseData && (
                  <div className="flex items-center space-x-3 bg-gradient-to-r from-secondary/30 to-amber-500/30 backdrop-blur-sm rounded-xl px-4 py-3 border-2 border-secondary/40 shadow-lg">
                    <div className="p-2 bg-secondary/40 rounded-xl flex-shrink-0">
                      <Icon name="TrendingUp" size={18} className="text-secondary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-text-secondary/70 font-medium uppercase tracking-wide">🏆 Mayor Compra (2° Premio)</div>
                      <div className="font-semibold text-sm text-secondary leading-tight">
                        {maxPurchaseData.cantidad} boletos
                      </div>
                      <div className="text-xs text-text-secondary/60">
                        Comprado el {maxPurchaseData.fecha}
                      </div>
                    </div>
                  </div>
                )}

                {/* Botón móvil - RESPONSIVE OPTIMIZADO PARA MÓVIL */}
                <div className="pt-2 sm:pt-3">
                  <Button
                    variant="secondary"
                    size="lg"
                    onClick={onPurchaseClick}
                    disabled={!isRaffleActive}
                    iconName="ShoppingCart"
                    iconPosition="left"
                    className="w-full text-sm sm:text-base px-4 py-3.5 sm:py-4 bg-gradient-to-r from-secondary to-amber-500 hover:from-secondary/90 hover:to-amber-400 text-gray-900 font-bold rounded-xl shadow-2xl transform hover:scale-105 transition-all duration-300 min-h-[48px]">

                    {isRaffleActive ? 'Comprar Boletos Ahora' : 'Sorteo Finalizado'}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - IMAGEN CON FLYER INTEGRADO - RESPONSIVE OPTIMIZADO PARA MÓVIL */}
          <div className="relative mt-6 lg:mt-0 px-0 lg:pr-4 xl:pr-6 2xl:pr-12">
            <div className="relative group">
              {/* Glow Effect Mejorado */}
              <div className="absolute -inset-3 lg:-inset-6 bg-gradient-to-r from-secondary/40 to-golden/40 rounded-xl lg:rounded-2xl xl:rounded-3xl blur-xl lg:blur-2xl xl:blur-3xl opacity-70 group-hover:opacity-90 transition-all duration-500"></div>
              
              {/* Main Image Container - RESPONSIVE OPTIMIZADO PARA MÓVIL + FLYER MÁS PROMINENTE */}
              <div className="relative bg-gradient-to-br from-card/95 to-card/85 backdrop-blur-md rounded-xl lg:rounded-2xl xl:rounded-3xl p-4 sm:p-5 lg:p-7 xl:p-9 border-2 border-golden/40 shadow-2xl">
                {/* LA IMAGEN - RESPONSIVE OPTIMIZADO PARA MÓVIL + ALTURA AUMENTADA PARA FLYER VISIBLE */}
                <Image
                  src={displayData.image}
                  alt={displayData?.imageAlt}
                  className="w-full h-56 sm:h-64 md:h-72 lg:h-80 xl:h-96 2xl:h-[28rem] object-cover rounded-lg lg:rounded-xl xl:rounded-2xl shadow-xl"
                  onError={(e) => {
                    console.log('⚠️ Flyer no encontrado, usando fallback automático');
                    e.target.src = displayData.fallbackImage;
                  }}
                  onLoad={() => {
                    setFlyerLoaded(true);
                    console.log('✅ Imagen cargada exitosamente');
                  }}
                />
                
                {/* Overlay Badge Mejorado - RESPONSIVE OPTIMIZADO PARA MÓVIL */}
                <div className="absolute top-2 sm:top-3 lg:top-4 right-2 sm:right-3 lg:right-4 bg-gradient-to-r from-secondary to-amber-500 text-gray-900 px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 lg:py-2.5 rounded-full shadow-2xl border border-golden/30">
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <Icon name="Star" size={12} className="sm:w-4 sm:h-4 lg:w-5 lg:h-5" />
                    <span className="font-heading font-bold text-xs sm:text-sm lg:text-base">
                      {flyerLoaded ? 'Premio Principal' : 'Imagen del Sorteo'}
                    </span>
                  </div>
                </div>

                {/* Bottom Info Mejorada - RESPONSIVE OPTIMIZADO PARA MÓVIL */}
                <div className="absolute bottom-2 sm:bottom-3 lg:bottom-4 left-2 sm:left-3 lg:left-4 right-2 sm:right-3 lg:right-4 bg-gradient-to-r from-background/95 to-card/95 backdrop-blur-sm rounded-lg lg:rounded-xl xl:rounded-2xl p-2 sm:p-3 lg:p-4 xl:p-5 border border-golden/30 shadow-xl">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-1.5 sm:space-y-2">
                    <div className="space-y-0.5 sm:space-y-1">
                      <h3 className="font-heading font-bold text-sm sm:text-base lg:text-lg xl:text-xl text-foreground">
                        {flyerLoaded ? 'Flyer Promocional' : 'Gran Premio'}
                      </h3>
                      <p className="text-xs sm:text-sm lg:text-base text-text-secondary font-medium">
                        {displayData?.drawDate}
                      </p>
                    </div>
                    <div className="text-left sm:text-right space-y-0.5 sm:space-y-1">
                      <div className="text-base sm:text-lg lg:text-xl xl:text-2xl font-heading font-bold text-secondary">
                        {displayData?.mainPrize}
                      </div>
                      <div className="text-xs sm:text-sm text-text-secondary font-medium">
                        Por boleto: Bs. {displayData?.ticketPrice?.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Elements Animados - RESPONSIVE OPTIMIZADO PARA MÓVIL */}
            <div className="absolute -top-3 sm:-top-4 lg:-top-6 -left-3 sm:-left-4 lg:-left-6 w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-secondary/30 rounded-full blur-lg lg:blur-xl animate-pulse"></div>
            <div className="absolute -bottom-3 sm:-bottom-4 lg:-bottom-6 -right-3 sm:-right-4 lg:-right-6 w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 bg-golden/30 rounded-full blur-lg lg:blur-xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute top-1/2 -left-4 sm:-left-5 lg:-left-8 w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 bg-secondary/20 rounded-full blur-md lg:blur-lg animate-pulse" style={{ animationDelay: '2s' }}></div>
          </div>
        </div>
      </div>
      
      {/* Scroll Indicator Mejorado - RESPONSIVE OPTIMIZADO PARA MÓVIL */}
      <div className="absolute bottom-3 sm:bottom-4 lg:bottom-6 left-1/2 transform -translate-x-1/2">
        <div className="flex flex-col items-center space-y-1.5 sm:space-y-2 text-text-secondary animate-bounce">
          <span className="text-xs font-medium bg-card/50 rounded-full px-2 py-1">Desliza para ver más</span>
          <Icon name="ChevronDown" size={16} className="text-secondary sm:w-5 sm:h-5" />
        </div>
      </div>
    </div>
  );
};

export default HeroBanner;